import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, vModelSelect as _vModelSelect, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "container-fluid" }
const _hoisted_2 = { class: "card" }
const _hoisted_3 = { class: "card-header" }
const _hoisted_4 = { class: "row align-items-center" }
const _hoisted_5 = { class: "col-auto" }
const _hoisted_6 = { class: "card-body border-bottom" }
const _hoisted_7 = { class: "row g-3" }
const _hoisted_8 = { class: "col-md-3" }
const _hoisted_9 = { class: "col-md-3" }
const _hoisted_10 = { class: "col-md-3" }
const _hoisted_11 = { class: "col-md-3" }
const _hoisted_12 = { class: "col-md-3" }
const _hoisted_13 = { class: "col-md-3" }
const _hoisted_14 = { class: "col-md-3" }
const _hoisted_15 = { class: "card-body" }
const _hoisted_16 = {
  key: 0,
  class: "text-center py-5"
}
const _hoisted_17 = {
  key: 1,
  class: "table-responsive"
}
const _hoisted_18 = { class: "table table-bordered table-striped" }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = {
  key: 2,
  class: "d-flex justify-content-between align-items-center mt-3"
}
const _hoisted_21 = { class: "text-muted" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = ["disabled"]
const _hoisted_24 = { class: "me-2" }
const _hoisted_25 = ["disabled"]

import { ref, onMounted, watch } from 'vue';
import { useToast } from 'vue-toast-notification';
import { format } from 'date-fns';
import { fetchGetApi } from "@/util/api";

interface ReportEmployee {
    id: number;
    employeeId: string;
    name: string;
    status: string;
    department: string;
    siteName: string;
    type: string;
    post: string;
    operationManager: string;
    remainingDays: number;
    createdAt: string;
    endDate: string;
    attendance: {
        summary: {
            totalDaysAttended: number;
            totalDaysAbsent: number;
        };
    };
}

interface Statistics {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    recordsInCurrentPage: number;
    averageDaysRemaining: string;
    statusBreakdown: Record<string, number>;
    typeBreakdown: Record<string, number>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'chase-list-report',
  setup(__props) {

const toast = useToast();
const employees = ref<ReportEmployee[]>([]);
const loading = ref(false);
const statistics = ref<Statistics | null>(null);

// Filter states
const filters = ref({
    siteId: '',
    siteName: '',
    employeeName: '',
    department: '',
    manager: '',
    status: '',
    type: '',
    employeeType: '',
    minDaysRemaining: '',
    maxDaysRemaining: '',
    startDate: '',
    endDate: '',
    sortBy: 'RemainingDaysOfChase',
    sortOrder: 'asc'
});

const currentPage = ref(1);
const pageLimit = ref(20);
const totalRecords = ref(0);

const formatDate = (date: string) => {
    return format(new Date(date), 'dd/MM/yyyy');
};

const calculateEndDate = (createdAt: string, remainingDays: number) => {
    if (!createdAt) return '';
    const startDate = new Date(createdAt);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + remainingDays);
    return endDate.toISOString();
};

const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
        'Active': 'badge bg-success',
        'Inactive': 'badge bg-danger',
        'In Progress': 'badge bg-warning',
        'Pending': 'badge bg-info'
    };
    return classes[status] || 'badge bg-secondary';
};

const fetchReport = async () => {
    loading.value = true;
    try {
        // Build query parameters
        const params = new URLSearchParams();
        
        Object.entries(filters.value).forEach(([key, value]) => {
            if (value) {
                params.append(key, value.toString());
            }
        });
        
        params.append('page', currentPage.value.toString());
        params.append('limit', pageLimit.value.toString());

        const queryString = params.toString();
        const endpoint = `api/admin/get/chase/list/report${queryString ? '?' + queryString : ''}`;
        
        const response = await fetchGetApi(endpoint);
        
        if (response?.data?.success && response.data.data) {
            employees.value = response.data.data.map((item: any) => {
                const createdAt = item.createdAt || item.CreatedAt || '';
                const remainingDays = item.RemainingDaysOfChase || 0;
                return {
                    id: item._id,
                    employeeId: item.empId,
                    name: item.Name,
                    status: item.Status || 'N/A',
                    department: item.Dept,
                    type: item.Type,
                    siteName: item.Site || 'N/A',
                    post: item.Post,
                    operationManager: item.OperationManagerName,
                    remainingDays: remainingDays,
                    createdAt: createdAt,
                    endDate: calculateEndDate(createdAt, remainingDays),
                    attendance: {
                        summary: {
                            totalDaysAttended: item.attendance?.summary?.totalDaysAttended || 0,
                            totalDaysAbsent: item.attendance?.summary?.totalDaysAbsent || 0
                        }
                    }
                };
            });
            
            // Store statistics
            if (response.data.statistics) {
                statistics.value = response.data.statistics;
                totalRecords.value = response.data.statistics.totalRecords;
            } else {
                totalRecords.value = employees.value.length;
            }
        } else {
            toast.error('Failed to fetch report data');
        }
    } catch (error) {
        console.error('Error fetching chase list report:', error);
        toast.error('Failed to fetch chase list report');
    } finally {
        loading.value = false;
    }
};

const resetFilters = () => {
    filters.value = {
        siteId: '',
        siteName: '',
        employeeName: '',
        department: '',
        manager: '',
        status: '',
        type: '',
        employeeType: '',
        minDaysRemaining: '',
        maxDaysRemaining: '',
        startDate: '',
        endDate: '',
        sortBy: 'RemainingDaysOfChase',
        sortOrder: 'asc'
    };
    currentPage.value = 1;
};

const changePage = (page: number) => {
    currentPage.value = page;
    fetchReport();
};

// Watch filters for automatic application
watch(filters, () => {
    currentPage.value = 1;
    fetchReport();
}, { deep: true });

onMounted(() => {
    fetchReport();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _cache[10] || (_cache[10] = _createElementVNode("div", { class: "col" }, [
            _createElementVNode("h5", null, "Chase List Report")
          ], -1)),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_router_link, {
              to: "/chase-list/list",
              class: "btn btn-secondary btn-sm"
            }, {
              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                _createElementVNode("i", { class: "fa fa-arrow-left me-1" }, null, -1),
                _createTextVNode(" Back to Chase List ", -1)
              ]))]),
              _: 1
            })
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _cache[11] || (_cache[11] = _createElementVNode("label", { class: "form-label" }, "Employee Name", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "text",
              class: "form-control form-control-sm",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.value.employeeName) = $event)),
              placeholder: "Search by name"
            }, null, 512), [
              [_vModelText, filters.value.employeeName]
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _cache[12] || (_cache[12] = _createElementVNode("label", { class: "form-label" }, "Site Name", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "text",
              class: "form-control form-control-sm",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.siteName) = $event)),
              placeholder: "Search by site"
            }, null, 512), [
              [_vModelText, filters.value.siteName]
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _cache[14] || (_cache[14] = _createElementVNode("label", { class: "form-label" }, "Status", -1)),
            _withDirectives(_createElementVNode("select", {
              class: "form-select form-select-sm",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.status) = $event))
            }, [...(_cache[13] || (_cache[13] = [
              _createElementVNode("option", { value: "" }, "All Status", -1),
              _createElementVNode("option", { value: "Active" }, "Active", -1),
              _createElementVNode("option", { value: "Inactive" }, "Inactive", -1),
              _createElementVNode("option", { value: "In Progress" }, "In Progress", -1)
            ]))], 512), [
              [_vModelSelect, filters.value.status]
            ])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _cache[16] || (_cache[16] = _createElementVNode("label", { class: "form-label" }, "Type", -1)),
            _withDirectives(_createElementVNode("select", {
              class: "form-select form-select-sm",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.value.type) = $event))
            }, [...(_cache[15] || (_cache[15] = [
              _createElementVNode("option", { value: "" }, "All Types", -1),
              _createElementVNode("option", { value: "Joining" }, "Joining", -1),
              _createElementVNode("option", { value: "Reinstate" }, "Reinstate", -1)
            ]))], 512), [
              [_vModelSelect, filters.value.type]
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _cache[17] || (_cache[17] = _createElementVNode("label", { class: "form-label" }, "Start Date", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "date",
              class: "form-control form-control-sm",
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((filters.value.startDate) = $event))
            }, null, 512), [
              [_vModelText, filters.value.startDate]
            ])
          ]),
          _createElementVNode("div", _hoisted_13, [
            _cache[18] || (_cache[18] = _createElementVNode("label", { class: "form-label" }, "End Date", -1)),
            _withDirectives(_createElementVNode("input", {
              type: "date",
              class: "form-control form-control-sm",
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filters.value.endDate) = $event))
            }, null, 512), [
              [_vModelText, filters.value.endDate]
            ])
          ]),
          _createElementVNode("div", _hoisted_14, [
            _cache[20] || (_cache[20] = _createElementVNode("label", { class: "form-label" }, "Sort Order", -1)),
            _withDirectives(_createElementVNode("select", {
              class: "form-select form-select-sm",
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((filters.value.sortOrder) = $event))
            }, [...(_cache[19] || (_cache[19] = [
              _createElementVNode("option", { value: "asc" }, "Ascending", -1),
              _createElementVNode("option", { value: "desc" }, "Descending", -1)
            ]))], 512), [
              [_vModelSelect, filters.value.sortOrder]
            ])
          ]),
          _createElementVNode("div", { class: "col-12" }, [
            _createElementVNode("button", {
              class: "btn btn-secondary btn-sm",
              onClick: resetFilters
            }, [...(_cache[21] || (_cache[21] = [
              _createElementVNode("i", { class: "fa fa-refresh me-1" }, null, -1),
              _createTextVNode(" Reset Filters ", -1)
            ]))])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_15, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_16, [...(_cache[22] || (_cache[22] = [
              _createElementVNode("div", {
                class: "spinner-border",
                role: "status"
              }, [
                _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
              ], -1)
            ]))]))
          : (_openBlock(), _createElementBlock("div", _hoisted_17, [
              _createElementVNode("table", _hoisted_18, [
                _cache[24] || (_cache[24] = _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", null, "Name"),
                    _createElementVNode("th", null, "Status"),
                    _createElementVNode("th", null, "Dept."),
                    _createElementVNode("th", null, "Type"),
                    _createElementVNode("th", null, "Site Name"),
                    _createElementVNode("th", null, "Post"),
                    _createElementVNode("th", null, "Days Attended"),
                    _createElementVNode("th", null, "Operation Manager"),
                    _createElementVNode("th", null, "Remaining Days"),
                    _createElementVNode("th", null, "Joining Date"),
                    _createElementVNode("th", null, "End Date")
                  ])
                ], -1)),
                _createElementVNode("tbody", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(employees.value, (employee) => {
                    return (_openBlock(), _createElementBlock("tr", {
                      key: employee.id
                    }, [
                      _createElementVNode("td", null, _toDisplayString(employee.name), 1),
                      _createElementVNode("td", null, [
                        _createElementVNode("span", {
                          class: _normalizeClass(getStatusClass(employee.status))
                        }, _toDisplayString(employee.status), 3)
                      ]),
                      _createElementVNode("td", null, _toDisplayString(employee.department), 1),
                      _createElementVNode("td", {
                        style: _normalizeStyle(employee.type === 'Siddar' ? 'color: red; background-color: rgba(255,0,0,0.1)' : '')
                      }, _toDisplayString(employee.type), 5),
                      _createElementVNode("td", null, _toDisplayString(employee.siteName), 1),
                      _createElementVNode("td", null, _toDisplayString(employee.post), 1),
                      _createElementVNode("td", null, _toDisplayString(employee.attendance.summary.totalDaysAttended), 1),
                      _createElementVNode("td", null, _toDisplayString(employee.operationManager), 1),
                      _createElementVNode("td", null, _toDisplayString(employee.remainingDays), 1),
                      _createElementVNode("td", null, _toDisplayString(employee.createdAt ? formatDate(employee.createdAt) : 'N/A'), 1),
                      _createElementVNode("td", null, _toDisplayString(employee.endDate ? formatDate(employee.endDate) : 'N/A'), 1)
                    ]))
                  }), 128)),
                  (employees.value.length === 0)
                    ? (_openBlock(), _createElementBlock("tr", _hoisted_19, [...(_cache[23] || (_cache[23] = [
                        _createElementVNode("td", {
                          colspan: "11",
                          class: "text-center text-muted"
                        }, "No records found", -1)
                      ]))]))
                    : _createCommentVNode("", true)
                ])
              ])
            ])),
        (employees.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
              _createElementVNode("div", null, [
                _createElementVNode("small", _hoisted_21, [
                  _createTextVNode(" Showing " + _toDisplayString(((currentPage.value - 1) * pageLimit.value) + 1) + " to " + _toDisplayString(Math.min(currentPage.value * pageLimit.value, totalRecords.value)) + " of " + _toDisplayString(totalRecords.value) + " records ", 1),
                  (statistics.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_22, " | Page " + _toDisplayString(statistics.value.currentPage) + " of " + _toDisplayString(statistics.value.totalPages), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("button", {
                  class: "btn btn-sm btn-outline-primary me-2",
                  onClick: _cache[7] || (_cache[7] = ($event: any) => (changePage(currentPage.value - 1))),
                  disabled: currentPage.value === 1
                }, " Previous ", 8, _hoisted_23),
                _createElementVNode("span", _hoisted_24, "Page " + _toDisplayString(currentPage.value), 1),
                _createElementVNode("button", {
                  class: "btn btn-sm btn-outline-primary",
                  onClick: _cache[8] || (_cache[8] = ($event: any) => (changePage(currentPage.value + 1))),
                  disabled: statistics.value ? currentPage.value >= statistics.value.totalPages : employees.value.length < pageLimit.value
                }, " Next ", 8, _hoisted_25)
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ]))
}
}

})